import { faArrowsRotate, faPrint } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDispatch, useSelector } from '@redux';
import { exportWS } from '@redux/orders/actions';
import { Button, Modal, Space } from 'antd';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ReactToPrint, { useReactToPrint } from 'react-to-print';
import styled, { createGlobalStyle } from 'styled-components';
import { genLargeLabelPackage } from 'templates/largeLabelPackage';
import { genOrderTemplate } from 'templates/phieuxuatkho';
import { genSmallLabelPackage } from 'templates/smallLabelPackage';
import { DynamicObjectType } from 'types/commons';
import 'jspdf/dist/polyfills.es.js';
import { jsPDF } from 'jspdf';
import { flatten, isEmpty, last, omit } from 'lodash';
import { lotDatePattern } from 'constants/constants';
import JsBarcode from 'jsbarcode';
import QRCode from 'qrcode';

interface IPrintLabelProps {
  disabled: boolean;
  hasRefresh: boolean;
  printActions: any[];
  orders: any;
  hasWsp: boolean;
  selectedRowKeys: any[];
}

const PRINT_CONTENT: any = {
  printDeliveryBill: genOrderTemplate,
  printLabelBig: genLargeLabelPackage,
  printLabelSmall: genSmallLabelPackage,
};

const PRINT_SIZE = {
  printDeliveryBill: undefined,
  printLabelBig: `@page {
    size: 10cm 15cm;
    color: #000  !important;
  }`,
  printLabelSmall: `@page {
    size: 4in 4in;
    color: #000  !important;
  }`,
};

const PRINT_BUTTON = {
  printDeliveryBill: {
    className: 'btn-dark',
    icon: <FontAwesomeIcon icon={faPrint} />,
  },
  printLabelBig: {
    className: 'btn-dark',
    icon: <FontAwesomeIcon icon={faPrint} />,
  },
  printLabelSmall: {
    className: 'btn-dark',
    icon: <FontAwesomeIcon icon={faPrint} />,
  },
};

const PrintLabel = (
  {
    disabled,
    hasRefresh,
    printActions = [],
    orders = {},
    hasWsp,
    selectedRowKeys,
  }: IPrintLabelProps,
  ref,
) => {
  console.log(orders)
  const [selected, setSelected] = useState();
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const dispatch = useDispatch();
  // const history = useHistory();
  const navigate = useNavigate();
  const currentBatch = useSelector<DynamicObjectType>(
    (state) => state.orderBatches.currentData,
  );
  const exportLoading = useSelector((state) => state.orders.exportLoading);

  const { t } = useTranslation();
  const printViewRef = useRef();
  const handlePrint = useReactToPrint({
    content: () => printViewRef.current,
  });

  const selectPrint = (type) => {
    setSelected(type);
    setVisible(true);
  };

  const handleWS = () => {
    dispatch(
      exportWS({
        data: {
          id: currentBatch?.id,
        },
      }),
    ).then(({ payload }) => {
      if (payload) {
        navigate(`/wspOrders`);
      }
    });
  };

  useImperativeHandle(ref, () => ({}), []);

  const downloadPDF = () => {
    setLoading(true);
    const doc = new jsPDF({
      orientation: 'p',
      unit: 'px',
      format: [800, 1228],
    });
    doc.setFont('Poppins');
    doc.setFontSize(12);
    doc.setLineHeightFactor(1.5);
    doc.html(printViewRef.current, {
      callback: function (doc) {
        doc.save();
        setLoading(false);
      },
    });
  };

  return (
    <div>
      <div className="d-flex column">
        <Space wrap align="center">
          {hasWsp && (
            <Button
              onClick={() => handleWS()}
              loading={exportLoading}
              className="btn-blue"
              disabled={disabled}
            >
              {t('orders.button.exportWarehouseShipment')}
            </Button>
          )}
          {printActions.map((e) => (
            <Button
              key={e}
              onClick={() => selectPrint(e)}
              {...PRINT_BUTTON[e]}
              disabled={disabled}
            >
              {t(`orders.button.${e}`)}
            </Button>
          ))}
          {hasRefresh && (
            <Button
              onClick={() => window.location.reload()}
              icon={<FontAwesomeIcon icon={faArrowsRotate} />}
            >
              {t('button.refresh')}
            </Button>
          )}
        </Space>
      </div>

      <ModalWrapper
        title={t('orders.reviewPrint')}
        footer={null}
        onCancel={() => setVisible(false)}
        destroyOnClose
        visible={visible}
        width="23cm"
        {...(!!selected && {
          footer:
            'printDeliveryBill' === selected
              ? [
                  <Button
                    onClick={() => downloadPDF()}
                    loading={loading}
                    className="btn-blue"
                    key="1"
                  >
                    Download PDF
                  </Button>,
                  <Button
                    key="print"
                    onClick={handlePrint}
                    type="primary"
                    icon={<FontAwesomeIcon icon={faPrint} />}
                  >
                    PRINT
                  </Button>,
                ]
              : [
                  <Button
                    key="print"
                    onClick={handlePrint}
                    type="primary"
                    icon={<FontAwesomeIcon icon={faPrint} />}
                  >
                    PRINT
                  </Button>,
                ],
        })}
      >
        <div className="d-flex column center text-align:right;">
          {!!selected && (
            <>
              <ReactToPrint
                // trigger={() => <button>Print this out!</button>}
                content={() => printViewRef.current}
                pageStyle={PRINT_SIZE[selected]}
              />
              <div ref={printViewRef}>
                {visible && selectedRowKeys?.length > 0 && !isEmpty(orders) && (
                  <PrintContent
                    orders={orders}
                    selectedRowKeys={selectedRowKeys}
                    selected={selected}
                    content={selectedRowKeys.map((e) => {
                      return PRINT_CONTENT[selected]?.(
                        {
                          ...orders[e],
                          items: flatten(
                            orders?.[e]?.items?.map((e) => {
                              return [
                                ...(e?.product?.comboItems?.length > 0
                                  ? e?.product?.comboItems?.map(
                                      (comboItem) => ({
                                        ...comboItem?.product,
                                        ...comboItem,
                                        quantity:
                                          +comboItem?.quantity * +e?.quantity,
                                        comboProduct: omit(e, ['comboItems']),
                                        isComboItem: true,
                                        name: comboItem.productName,
                                        currency: 'đ',
                                        thumbnail:
                                          comboItem?.variant?.thumbnail ||
                                          comboItem.productThumbnail ||
                                          comboItem?.propduct?.thumbnail,
                                      }),
                                    ) || []
                                  : [
                                      {
                                        ...e,
                                        name: e.productName,
                                        quantity: e.quantity,
                                        price: Number(e.price),
                                        currency: 'đ',
                                        thumbnail:
                                          e?.variant?.thumbnail ||
                                          e.productThumbnail ||
                                          e?.propduct?.thumbnail,
                                        // comboItems: ,
                                      },
                                    ]),
                              ];
                            }) || [],
                          )?.map((item: DynamicObjectType) => {
                            const lotDate: string = last(
                              item?.variant?.sku?.split('_'),
                            );
                            const isHasLotDate = lotDatePattern.test(lotDate);
                            const variantSku = isHasLotDate
                              ? item?.variant?.sku?.replace(`_${lotDate}`, '')
                              : item?.variant?.sku;

                            return {
                              ...item,
                              ...orders[
                                e
                              ]?.dataFromErp?.OrderCreateResult?.Lines?.find(
                                (erp) =>
                                  `${erp?.ItemId}${
                                    erp?.VariantId ? `_${erp?.VariantId}` : ''
                                  }` === variantSku,
                              ),
                            };
                          }),
                        },
                        currentBatch,
                      );
                    })}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </ModalWrapper>
    </div>
  );
};

export default forwardRef(PrintLabel);

const ModalWrapper = styled(Modal)`
  .ant-modal-body {
    max-height: 60vh;
    overflow: auto;
  }
`;

export const PrintContent = ({
  selected,
  selectedRowKeys,
  orders,
  content,
}) => {
  const [_content, setContent] = useState('');

  useEffect(() => {
    let tmp = '';
    Promise.all(content).then((res) => {
      res.map((r) => {
        tmp += r;
      });
      console.log('tmp',tmp)
      setContent(tmp);
    });
  }, [content]);

  useEffect(() => {
    if (selectedRowKeys?.length && !isEmpty(orders) && _content) {
      if (selected === 'printLabelBig' || selected === 'printLabelSmall') {
        selectedRowKeys.forEach((e) => {
          if (orders?.[e]?.channel === 'shopee') {
            document.getElementById(
              `shopee-id-${orders[e]?.shippingInfo?.tracking_number}`,
            ) &&
              JsBarcode(
                `#shopee-id-${orders[e]?.shippingInfo?.tracking_number}`,
                orders[e]?.shippingInfo?.tracking_number,
                {
                  displayValue: false,
                },
              );
          }
          if (
            document.getElementById(`qr-tracking-${e}`) &&
            orders?.[e]?.channel === 'shopee'
          ) {
            const canvas = document.getElementById(`qr-tracking-${e}`);

            QRCode.toCanvas(canvas, orders[e]?.shippingInfo?.tracking_number);
          }
          if (document.getElementById(`qr-sortcode-${e}`)) {
            const canvas = document.getElementById(`qr-sortcode-${e}`);

            QRCode.toCanvas(canvas, orders[e]?.sortCode);
          }
          if (document.getElementById(`qr-ordernumber-${e}`)) {
            const canvas = document.getElementById(`qr-ordernumber-${e}`);

            QRCode.toCanvas(canvas, orders[e]?.orderNumber);
          }
          document.getElementById(`customer-order-id-${e}`) &&
            JsBarcode(`#customer-order-id-${e}`, orders[e]?.deliveryCode, {
              // format: 'CODE39',
              // height: 240,
              // width: 8,a
              displayValue: false,
            });
          if (document.getElementById(`qr-routeCode-${e}`)) {
            const canvas = document.getElementById(`qr-routeCode-${e}`);

            QRCode.toCanvas(
              canvas,
              orders[e]?.shippingLabelData?.data?.routeCode,
            );
          }

          document.getElementById(`ordernumber-order-id-${e}`) &&
            JsBarcode(`#ordernumber-order-id-${e}`, orders[e]?.orderNumber, {
              // format: 'CODE39',
              // height: 240,
              // width: 8,a
              displayValue: false,
            });
          document.getElementById(`sale-order-id-${e}`) &&
            JsBarcode(`#sale-order-id-${e}`, orders[e]?.orderNumber, {
              // format: 'CODE39',
              // height: 280,
              // width: 8,
              displayValue: false,
            });
        });
      }
      if (selected === 'printDeliveryBill') {
        selectedRowKeys.forEach((e) => {
          document.getElementById(`sale-order-id-${e}`) &&
            JsBarcode(`#sale-order-id-${e}`, orders[e]?.saleOrderId, {
              // format: 'CODE39',
              // height: 240,
              // width: 8,
              displayValue: false,
            });
          if (document.getElementById(`site-url-${e}`)) {
            const canvas = document.getElementById(`site-url-${e}`);

            QRCode.toCanvas(
              canvas,
              `https://${
                orders[e]?.chainStore?.siteUrl || 'reebok.com.vn'
              }/help-center/questions`,
            );
          }
          // orders[e]?.items && orders[e]?.items?.map((item, index) => {
          //   (item?.variant?.barCode || item?.barCode || item?.barcode) && JsBarcode(`#item-barcode-${index}`, item?.variant?.barCode || item?.barCode || item?.barcode, {
          //     // format: 'CODE39',
          //     height: 240,
          //     width: 8,
          //     displayValue: false,
          //   })
          // })
        });
      }
    }
    // eslint-disable-next-line
  }, [_content]);

  return (
    <>
      <PrintContentWrapper
        dangerouslySetInnerHTML={{
          __html: _content,
        }}
        className={`page-break`}
      />
      {selected === 'printLabelSmall' && <GlobalStyleForPrintSmall />}
      {selected === 'printLabelBig' && <GlobalStyleForPrintLabelLarge />}
    </>
  );
};

const GlobalStyleForPrintSmall = createGlobalStyle`
@media print {
    @page {
      size: 4in 4in;
      margin: 0;
    }
}
    @page {
      size: 4in 4in;
      margin: 0;
    }
    `;

const GlobalStyleForPrintLabelLarge = createGlobalStyle`
  
    @media print {
        @page {
          size: 10cm 15cm;
          margin: 0;
        }
    }
        @page {
          size: 10cm 15cm;
          margin: 0;
        }
        `;

const PrintContentWrapper = styled.div`
  section {
    ${'' /* margin-top: 100px; */}
  }
  &.page-break {
    @media print {
      section {
        page-break-after: always;
        margin-top: 0px;
      }
    }
  }
  * {
    font-family: Poppins, sans-serif !important;
    color: #000 !important;
  }

  .order-qrcode-img {
    height: 60px !important;
    width: 60px !important;
    margin: -2px !important;
  }
  .site-qrcode-img {
    height: 82px !important;
    width: 82px !important;
    margin: -8px !important;
  }

  .barcode-img {
    height: 60px;
    width: 90%;
    max-height: 60px;
    &.shopee-tracking-number {
      width: 100%;
    }
    &.oder-number {
      width: 80%;
    }
  }
  .barcode-item-img {
    height: 60px;
    width: 140px;
    max-height: 60px;
  }
  .barcode-delivery-img {
    height: 96px;
    width: auto;
    max-width: 280px;
  }
`;
